/**
 * ChangeSubscriptionPlanForm — pick a paid tier + billing interval.
 * FinalForm body; Cancel + Save action row.
 *
 * Reusable across audiences: the caller passes `tierConfig` (a map of
 * tierKey -> { name, description, price, annualPrice, features }) and
 * `selectableTiers` (array of tier keys the user can pick). Defaults
 * point at the org TIER_CONFIG so existing org call-sites don't change.
 *
 * Save is disabled while the picked tier+interval matches the current
 * subscription. The page wraps this in <Modal>; the form owns its own
 * action row styling (display: grid; 1fr 1fr; gap 12px) per the
 * modal+form convention.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { TIER_CONFIG, SUBSCRIPTION_TIERS } from '../../util/subscriptions';
import { Form, FieldRadioButton, SecondaryButton, PrimaryButton } from '../../components';

import css from './ChangeSubscriptionPlanForm.module.css';

// Default selectable tiers for org callers. Freemium isn't here because
// the way to "switch to freemium" is to cancel; enterprise is contact-sales.
const DEFAULT_SELECTABLE_TIERS = [SUBSCRIPTION_TIERS.STARTER, SUBSCRIPTION_TIERS.PRO];

const formatPrice = (cents, interval) => {
  if (!cents && cents !== 0) return null;
  const amount = (cents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  const suffix = interval === 'annual' ? '/year' : '/month';
  return `${amount}${suffix}`;
};

const ChangeSubscriptionPlanFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        values,
        currentTier,
        currentInterval,
        changePlanInProgress,
        changePlanError,
        isCheckout,
        tierConfig = TIER_CONFIG,
        selectableTiers = DEFAULT_SELECTABLE_TIERS,
        onCancel,
      } = fieldRenderProps;

      const selectedTier = values?.tier;
      const selectedInterval = values?.interval || 'monthly';
      const unchanged = selectedTier === currentTier && selectedInterval === currentInterval;
      const submitDisabled = !selectedTier || unchanged || changePlanInProgress;

      const classes = classNames(rootClassName || css.root, className);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.intervalGroup}>
            <FieldRadioButton
              id="ChangeSubscriptionPlanForm.intervalMonthly"
              name="interval"
              value="monthly"
              label={<FormattedMessage id="ChangeSubscriptionPlanForm.monthly" />}
            />
            <FieldRadioButton
              id="ChangeSubscriptionPlanForm.intervalAnnual"
              name="interval"
              value="annual"
              label={<FormattedMessage id="ChangeSubscriptionPlanForm.annual" />}
            />
          </div>

          <div className={css.tierList}>
            {selectableTiers.map(tierKey => {
              const cfg = tierConfig[tierKey];
              if (!cfg) return null;
              const isCurrent = tierKey === currentTier;
              const priceCents = selectedInterval === 'annual' ? cfg.annualPrice : cfg.price;

              return (
                <label
                  key={tierKey}
                  htmlFor={`ChangeSubscriptionPlanForm.tier.${tierKey}`}
                  className={classNames(css.tierCard, {
                    [css.tierCardSelected]: selectedTier === tierKey,
                  })}
                >
                  <div className={css.tierHeader}>
                    <FieldRadioButton
                      id={`ChangeSubscriptionPlanForm.tier.${tierKey}`}
                      name="tier"
                      value={tierKey}
                      label={<span className={css.tierName}>{cfg.name}</span>}
                    />
                    {isCurrent ? (
                      <span className={css.currentBadge}>
                        <FormattedMessage id="ChangeSubscriptionPlanForm.currentBadge" />
                      </span>
                    ) : null}
                  </div>
                  <p className={css.tierDescription}>{cfg.description}</p>
                  <p className={css.tierPrice}>{formatPrice(priceCents, selectedInterval)}</p>
                  {cfg.features?.length ? (
                    <ul className={css.tierFeatures}>
                      {cfg.features.slice(0, 4).map(f => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  ) : null}
                </label>
              );
            })}
          </div>

          {changePlanError ? (
            <p className={css.error}>
              <FormattedMessage id="ChangeSubscriptionPlanForm.submitFailed" />
            </p>
          ) : null}

          <div className={css.actions}>
            <SecondaryButton type="button" onClick={onCancel} disabled={changePlanInProgress}>
              <FormattedMessage id="ChangeSubscriptionPlanForm.cancel" />
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              inProgress={changePlanInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage
                id={
                  isCheckout
                    ? 'ChangeSubscriptionPlanForm.upgrade'
                    : 'ChangeSubscriptionPlanForm.save'
                }
              />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

ChangeSubscriptionPlanFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  changePlanError: null,
  changePlanInProgress: false,
  currentTier: null,
  currentInterval: 'monthly',
};

const { bool, func, string } = PropTypes;

ChangeSubscriptionPlanFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  currentTier: string,
  currentInterval: string,
  changePlanInProgress: bool,
  changePlanError: PropTypes.any,
  // Optional — defaults to the org TIER_CONFIG + STARTER/PRO selectable
  // set. Pass CREATOR_TIER_CONFIG + [creator_pro] for the creator side.
  tierConfig: PropTypes.object,
  selectableTiers: PropTypes.arrayOf(string),
};

const ChangeSubscriptionPlanForm = compose(injectIntl)(ChangeSubscriptionPlanFormComponent);
ChangeSubscriptionPlanForm.displayName = 'ChangeSubscriptionPlanForm';

export default ChangeSubscriptionPlanForm;
